import { useState, useEffect, useCallback } from 'react'
import { BrowserRouter as Router, useNavigate, useLocation } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import { showSuccessToast, showErrorToast, TOAST_MESSAGES } from '@/utils/toasts'
import AuthRouter from '@/components/auth/AuthRouter'
import AppRouter from '@/components/layout/AppRouter'
import ScrollToTop from '@/components/layout/ScrollToTop'
import ErrorBoundary from '@/components/common/ErrorBoundary'
import ImpersonationBar from '@/components/common/ImpersonationBar'
import LoadingSpinner from '@/components/common/LoadingSpinner'
import { APIRoutes } from '@/services/api.routes'
import { getAllOwnedMessage, startCheckoutChain, takePendingCheckout } from '@/services/checkoutChain'
import { refreshTokenIfNeeded } from '@/services/tokenRefresh'
import { findSubscriptionProduct, loadSubscriptionProducts } from '@/services/subscription-products'
import { clearImpersonationData, isImpersonating } from '@/services/impersonation'
import { isFrauMeetsBoerseHost } from '@/consts/frauMeetsBoerse'
import FmbHostRouter from '@/components/frauMeetsBoerse/FmbHostRouter'
import { UserProvider } from '@/contexts/UserContext'
import { SubscriptionProductsProvider } from '@/contexts/SubscriptionProductsContext'
import { TourProvider } from '@/components/onboarding/TourProvider'
import { analytics } from '@/services/analytics'
import * as CookieConsent from 'vanilla-cookieconsent'
import { cookieConsentConfig } from '@/config/cookieConsent'
import 'vanilla-cookieconsent/dist/cookieconsent.css'
import '@/components/onboarding/tourStyles.css'

/**
 * Validates a redirect URL to prevent open redirect vulnerabilities.
 * Only allows relative URLs that start with '/' and don't contain protocol-relative URLs.
 *
 * @param url - The URL to validate
 * @returns true if the URL is safe to redirect to, false otherwise
 */
function isValidRedirectUrl(url: string): boolean {
  if (url === null || url === undefined || url.trim() === '') {
    return false
  }

  // Must start with '/' (relative URL)
  if (!url.startsWith('/')) {
    return false
  }

  // Reject protocol-relative URLs (e.g., '//evil.com')
  if (url.startsWith('//')) {
    return false
  }

  // Reject URLs with protocols (e.g., 'http://', 'javascript:', 'data:')
  return !url.includes(':');


}

function AppContent() {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [isCheckoutRedirecting, setIsCheckoutRedirecting] = useState<boolean>(false)
  const navigate = useNavigate()
  const location = useLocation()

  // Register unauthorized handler
  useEffect(() => {
    const handleUnauthorized = () => {
      setIsAuthenticated(false)
      // Save current URL as redirect parameter if not already on login page
      const currentPath = location.pathname + location.search
      if (location.pathname !== '/login' && location.pathname !== '/auth/register') {
        // Validate redirect URL before setting it
        if (isValidRedirectUrl(currentPath)) {
          navigate(`/login?redirect=${encodeURIComponent(currentPath)}`, { replace: true })
        } else {
          // Invalid redirect URL, navigate to login without redirect
          navigate('/login', { replace: true })
        }
      } else {
        navigate('/login', { replace: true })
      }
    }

    APIRoutes.setOnUnauthorized(handleUnauthorized)
  }, [navigate, location])

  useEffect(() => {
    const checkAuth = () => {
      const authenticated = APIRoutes.isAuthenticated()
      setIsAuthenticated(authenticated)
      setIsLoading(false)
      if (authenticated) {
        void refreshTokenIfNeeded()
      }
      // Note: Navigation for unauthenticated users is now handled by AuthRouter
      // This useEffect only sets the authentication state
    }

    checkAuth()
  }, [location, navigate])

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'visible' && APIRoutes.isAuthenticated()) {
        void refreshTokenIfNeeded()
      }
    }
    document.addEventListener('visibilitychange', onVisibility)
    return () => document.removeEventListener('visibilitychange', onVisibility)
  }, [])

  const handleLogin = useCallback((token: string) => {
    APIRoutes.setToken(token)
    setIsAuthenticated(true)

    // Read redirect parameter from current URL at runtime
    const currentSearchParams = new URLSearchParams(window.location.search)
    const redirectParam = currentSearchParams.get('redirect')
    const validRedirect = redirectParam !== null && isValidRedirectUrl(redirectParam) ? redirectParam : null

    // Pending plan(s) from a register link (Registration.tsx stores them before login/registration).
    // Always consumed, so a stale selection from an abandoned registration in this tab cannot
    // hijack a later login. An explicit ?redirect= (e.g. protected page -> login) takes precedence.
    const pendingCheckout = takePendingCheckout()
    if (pendingCheckout !== null && validRedirect === null) {
      setIsCheckoutRedirecting(true)

      // Async: validate plans against the backend products, fetch user, start the (possibly chained) checkout
      const startCheckout = async () => {
        try {
          const products = await loadSubscriptionProducts()
          const plans = pendingCheckout.plans.filter(plan => findSubscriptionProduct(products, plan) !== undefined)
          if (plans.length === 0) {
            throw new Error(`unknown subscription types: ${pendingCheckout.plans.join(',')}`)
          }
          const user = await APIRoutes.getCurrentUser()
          const result = await startCheckoutChain({
            user: user,
            plans: plans,
            coupon: pendingCheckout.coupon,
            referrerCode: pendingCheckout.referrerCode,
            source: 'registration',
            products: products,
          })
          if (result === 'ALL_OWNED') {
            // Bestandskunde, der über einen Register-Link kam und alles schon hat
            setIsCheckoutRedirecting(false)
            showErrorToast(getAllOwnedMessage(plans.length))
            navigate('/dashboard', { replace: true })
          }
        } catch (error) {
          console.error('Auto-checkout failed:', error)
          setIsCheckoutRedirecting(false)
          showErrorToast(TOAST_MESSAGES.GENERAL_ERROR)
          navigate('/', { replace: true })
        }
      }

      startCheckout()
      return
    }

    // Navigate to redirect URL if present and valid
    if (validRedirect !== null) {
      navigate(validRedirect, { replace: true })
    } else {
      // Navigate to dashboard after successful login/registration
      navigate('/', { replace: true })
    }
  }, [navigate])

  const handleLogout = useCallback(() => {
    // Track logout before resetting identity
    analytics.track('Logout')
    analytics.reset()
    APIRoutes.logout()
    // Clear any impersonation data to ensure clean state
    clearImpersonationData()
    setIsAuthenticated(false)
    showSuccessToast(TOAST_MESSAGES.LOGOUT_SUCCESS)
    // Navigate to login after logout
    navigate('/login', { replace: true })
  }, [navigate])

  // Externe Frau-meets-Börse-Domains (Reverse Proxy): eigener Routen-Baum,
  // ohne Auth-Routing, Spinner oder Toaster — jeder Pfad rendert die Landingpage.
  if (isFrauMeetsBoerseHost()) {
    return <FmbHostRouter />
  }

  if (isLoading) {
    return <LoadingSpinner message="Lade Börsencampus..." />
  }

  if (isCheckoutRedirecting) {
    return <LoadingSpinner message="Du wirst zum Checkout weitergeleitet..." />
  }

  return (
    <>
      <ImpersonationBar />
      {isAuthenticated ? (
        <UserProvider>
          <TourProvider>
            <AppRouter onLogout={handleLogout} />
          </TourProvider>
        </UserProvider>
      ) : (
        <AuthRouter onLogin={handleLogin} />
      )}
      <Toaster
        position="top-right"
        containerStyle={{
          top: isImpersonating() ? '108px' : '80px',
        }}
        toastOptions={{
          duration: 4000,
          style: {
            background: '#fff',
            color: '#374151',
            borderRadius: '0.5rem',
            border: '1px solid #e5e7eb',
            fontSize: '14px',
          },
          success: {
            iconTheme: {
              primary: '#10b981',
              secondary: '#fff',
            },
          },
          error: {
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />
    </>
  )
}

function App() {
  // Initialize cookie consent on app load
  useEffect(() => {
    CookieConsent.run(cookieConsentConfig)
  }, [])

  return (
    <ErrorBoundary>
      <Router>
        <ScrollToTop />
        <SubscriptionProductsProvider>
          <AppContent />
        </SubscriptionProductsProvider>
      </Router>
    </ErrorBoundary>
  )
}

export default App
